import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Footer from '../../../components/Footer/Footer';
import LogModal from '../../../components/Logs/LogModal';
import Menu from '../../../components/Menu/Menu';
import Toast from '../../../components/Toast/Toast';
import { getBacktest } from '../../../services/BacktestsService';
import ResultsTable from './ResultsTable';
import './Backtests.css';

// while the backtest is running the page polls for the result
const POLLING_MS = 5000;

function getStatusBadge(status) {
    if (status === 'running')
        return <span className="badge bg-info ms-1">RUNNING</span>;
    if (status === 'error')
        return <span className="badge bg-danger ms-1">ERROR</span>;
    return <span className="badge bg-success ms-1">DONE</span>;
}

function getDate(date) {
    return date ? new Date(date).toLocaleString() : '-';
}

function getCoin(coin) {
    if (coin === null || coin === undefined || coin === '') return '-';
    const value = Number(coin);
    return Number.isNaN(value) ? coin : value.toFixed(5);
}

function getConfigFields(backtest) {
    const fields = [
        ['Symbol', backtest.symbol],
        ['Start', getDate(backtest.startDate)],
        ['End', getDate(backtest.endDate)],
        ['Start Base', getCoin(backtest.startBase)],
        ['Start Quote', getCoin(backtest.startQuote)],
        ['Automations', backtest.description],
    ];

    return (
        <div className="row">
            {fields.map(([label, value]) => (
                <div className="col-md-3 mb-3" key={label}>
                    <div className="form-group">
                        <label>{label}:</label>
                        <div>{value}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function getRunAlert(backtest) {
    if (backtest.status === 'running')
        return (
            <div className="alert alert-info d-flex justify-content-between py-2">
                <span>
                    Running backtest, this can take a few minutes — periods are
                    capped at 3 months. This page updates automatically.
                </span>
                <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                ></span>
            </div>
        );
    if (backtest.status === 'error')
        return (
            <div className="alert alert-danger py-2">
                {backtest.error || 'The backtest failed.'}
            </div>
        );
    return <></>;
}

function getResultsCard(backtest) {
    return (
        <div className="row mb-4">
            <div className="col-12">
                <div className="card border-0 shadow">
                    <div className="card-header">
                        <div className="row align-items-center">
                            <div className="col">
                                <h2 className="fs-5 fw-0 bold">Results</h2>
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        {backtest.status === 'done' && backtest.results ? (
                            <ResultsTable data={backtest} />
                        ) : (
                            <p className="text-gray-600 mb-0">
                                {backtest.status === 'done'
                                    ? 'No operations for this backtest.'
                                    : 'The results appear here when the run finishes.'}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function BacktestDetails() {
    const { id } = useParams();

    const [backtest, setBacktest] = useState(null);
    const [notification, setNotification] = useState({ type: '', text: '' });

    useEffect(() => {
        getBacktest(id)
            .then(result => setBacktest(result))
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }, [id]);

    useEffect(() => {
        if (!backtest || backtest.status !== 'running') return;

        const timer = setInterval(() => {
            getBacktest(id)
                .then(result => setBacktest(result))
                .catch(err => {
                    console.error(
                        err.response ? err.response.data : err.message,
                    );
                    setNotification({
                        type: 'error',
                        text: err.response ? err.response.data : err.message,
                    });
                });
        }, POLLING_MS);

        return () => clearInterval(timer);
    }, [backtest && backtest.status, id]);

    return (
        <>
            <Menu />
            <main className="content">
                <div className="card card-body border-0 shadow mb-4 mt-4">
                    <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                        <div className="d-block mb-4 mb-md-0">
                            <h2 className="h4">
                                Backtest #{id}
                                {backtest ? (
                                    getStatusBadge(backtest.status)
                                ) : (
                                    <></>
                                )}
                            </h2>
                            {backtest ? (
                                <small className="text-gray-500">
                                    Created at {getDate(backtest.createdAt)}
                                </small>
                            ) : (
                                <></>
                            )}
                        </div>
                        <div className="btn-toolbar mb-2 mb-md-0">
                            <div className="d-inline-flex align-items-center">
                                {backtest ? (
                                    <button
                                        type="button"
                                        className="btn btn-info me-2"
                                        title="Logs from this Backtest"
                                        data-bs-toggle="modal"
                                        data-bs-target="#modalLogs"
                                    >
                                        <FontAwesomeIcon
                                            icon={faFileAlt}
                                            className="icon icon-xs me-1"
                                        />
                                        Logs
                                    </button>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                    </div>
                    {backtest ? (
                        <>
                            {getRunAlert(backtest)}
                            {getConfigFields(backtest)}
                        </>
                    ) : (
                        <p className="text-gray-600 mb-0">Loading...</p>
                    )}
                </div>
                {backtest ? getResultsCard(backtest) : <></>}
                <Footer />
            </main>
            <LogModal file={backtest ? 'backtest-' + backtest.userId : ''} />
            <Toast
                type={notification.type}
                text={notification.text}
                onShow={() => setNotification({})}
            />
        </>
    );
}

export default BacktestDetails;
